import React from 'react';

// Minimal, dependency-free Markdown renderer for blog posts. Handles exactly
// the subset used in src/data/blogPosts.js: "##"/"###" headings, paragraphs,
// "-" and "1." lists, **bold**, and [text](url) links. Parses line-by-line so
// a label line directly above a list (no blank line between) renders correctly.
//
// Why not react-markdown? It throws during react-snap's prerender pass, which
// produces empty (un-indexable) static HTML for our blog routes. Rendering the
// markdown ourselves keeps the prerendered output correct and complete.

// Parse inline **bold** and [text](url) into an array of React nodes.
function renderInline(text) {
  const nodes = [];
  const pattern = /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/;
  let remaining = text;
  let key = 0;
  let m;
  while ((m = pattern.exec(remaining))) {
    const before = remaining.slice(0, m.index);
    if (before) nodes.push(<React.Fragment key={key++}>{before}</React.Fragment>);
    if (m[1]) {
      nodes.push(<strong key={key++}>{m[2]}</strong>);
    } else {
      nodes.push(<a key={key++} href={m[5]}>{m[4]}</a>);
    }
    remaining = remaining.slice(m.index + m[0].length);
  }
  if (remaining) nodes.push(<React.Fragment key={key++}>{remaining}</React.Fragment>);
  return nodes;
}

export default function Markdown({ children, text }) {
  // Accept either <Markdown>{md}</Markdown> or <Markdown text={md} /> — the
  // admin money pages use the `text` prop, the marketing pages use children.
  const raw = children != null ? children : text;
  const source = (raw || '').replace(/\r\n/g, '\n').trim();
  const lines = source.split('\n');
  const elements = [];
  let para = [];
  let list = null; // { type: 'ul' | 'ol', items: [] }
  let key = 0;

  const flushPara = () => {
    if (para.length) {
      elements.push(<p key={key++}>{renderInline(para.join(' '))}</p>);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const Tag = list.type;
      elements.push(
        <Tag key={key++}>
          {list.items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </Tag>
      );
      list = null;
    }
  };
  const flushAll = () => {
    flushPara();
    flushList();
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushAll();
      continue;
    }
    if (line.startsWith('### ')) {
      flushAll();
      elements.push(<h3 key={key++}>{renderInline(line.slice(4))}</h3>);
      continue;
    }
    if (line.startsWith('## ')) {
      flushAll();
      elements.push(<h2 key={key++}>{renderInline(line.slice(3))}</h2>);
      continue;
    }
    const ulMatch = line.match(/^-\s+(.*)/);
    if (ulMatch) {
      flushPara();
      if (!list || list.type !== 'ul') {
        flushList();
        list = { type: 'ul', items: [] };
      }
      list.items.push(ulMatch[1]);
      continue;
    }
    const olMatch = line.match(/^\d+\.\s+(.*)/);
    if (olMatch) {
      flushPara();
      if (!list || list.type !== 'ol') {
        flushList();
        list = { type: 'ol', items: [] };
      }
      list.items.push(olMatch[1]);
      continue;
    }
    // Plain text line: ends any open list, accumulates into the paragraph.
    flushList();
    para.push(line);
  }
  flushAll();

  return <>{elements}</>;
}
